// Cart-endpoint API: /cart/preview (debounced read) + /cart/commit (mutation).
//
// Both are register-scoped v2 endpoints. The BE pipeline is identical for
// each -- preview rolls back, commit commits -- so the math the FE renders
// from preview matches what gets persisted at commit. See
// `hypnotoad/handlers/api/v2/cart.py` for the server side.
//
// This file deliberately does NOT touch posStore. State lives in
// `stores/cartStore.ts`; the React hook there is what calls these functions.

import { useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { v2, v2api, idempotencyKey, type V2Envelope } from './v2'
import type { Cart, CartDiscount, DiscountOverrideRow, PreviewResponse } from '@/lib/cart'
import { cartFingerprint } from '@/lib/cart'
import { attachApprovals, type DiscountApproval } from '@/lib/discountLimit'

/** /cart/preview request shape. Minimal -- the BE owns pricing math. */
export interface CartPreviewRequest {
  customer_id: number | null
  exempt_taxes: boolean
  items: Array<{
    product_id: number
    qty: number
    lot_number?: string | null
    /** Cashier-supplied unit price override. */
    price?: string | number | null
    /** Per-line dollar discount (legacy `discount_item` scope). The BE
     *  takes a primitive number — convert percent → amount before sending. */
    discount_amount?: number | null
    /** Gift-card identification + cashier-loaded balance. Required on
     *  /cart/preview when the line is a gift-card sale: gift cards have
     *  no catalog price for their SKU, so without these fields the BE
     *  reprices to $0. Persisted on the receipt-item by ops_receipt's
     *  add_item once /cart/commit lands. */
    gift_card_number?: string | null
    gift_card_balance?: number | null
    /** Scan-to-confirm-lot state. Persisted on the receipt-item by
     *  ops_receipt's add_item so an already-scanned cannabis line stays
     *  confirmed across recall / delivery-start (no re-prompt). */
    scan_confirmed?: boolean
    /** Cashier's reason for this line's discount (audit trail). */
    discount_reason_id?: number | null
    discount_reason_other?: string | null
    /** Approving manager's PIN for an over-limit line discount or manual
     *  price. Attached by `attachApprovals`; see lib/discountLimit.ts. */
    override_pin?: string
  }>
  /** Cashier-applied receipt-level discount, when present. The BE applies
   *  it as a final pass over the line totals and reports the result via
   *  `totals.discount_amount`. See V2-BACKEND-FOLLOWUPS.md § "Cart-level
   *  discount on /cart/preview + /cart/commit".
   *
   *  Mutually exclusive with per-line `discount_amount`: the BE silently
   *  drops one side when both are present. `cartToRequest` enforces this. */
  discount?: CartDiscount | null
  /** Bound receipt (recalled/unheld or partly paid). On preview the BE reads
   *  it to reuse manager approvals already recorded on that receipt, so a
   *  recalled over-limit discount doesn't 409. Added by fetchCartPreview. */
  receipt_id?: number
}

/** /cart/commit request shape. Outcome is `hold` xor `pay` xor
 *  `defer_payment` — exactly one. */
export interface CartCommitRequest extends CartPreviewRequest {
  idempotency_key: string
  /**
   * Optional: when set, the BE OVERWRITES the named (held) receipt's
   * items + JTs instead of creating a new one. Used by the unhold-edit
   * flow so a held receipt's id stays stable across the cashier's edits.
   */
  receipt_id?: number
  /** Set true to persist as a held receipt. Mutually exclusive with `pay`. */
  hold?: boolean
  /** Single-payment dict. Multi-tender (array) is Phase 2. */
  pay?: {
    payment_type: number
    amount: string | number
    confirm_overpayment?: boolean
    last4?: string
    notes?: string
    [k: string]: unknown
  }
  /** Set true to commit the receipt without recording any payment.
   *  Used by the integrated card-terminal flow: commit creates the
   *  receipt in `new` state, then the FE POSTs to
   *  `/receipts/{id}/terminal-payments` to start the session. Mutually
   *  exclusive with `pay` (sending both is a 400). */
  defer_payment?: boolean
  /** Duplicate-order guard override. The BE 409s with
   *  `possible_duplicate` when an identical basket (same products+qty,
   *  same register, same customer) was committed within the guard
   *  window (SysConfig `pos.v2.duplicate_guard_window_s`, default 300s)
   *  -- the signature of a cashier re-ring after a card-terminal
   *  hiccup. Set true ONLY after the cashier explicitly confirmed the
   *  "identical order just completed" dialog. The retry must carry a
   *  FRESH idempotency_key (this flag changes the body, so replaying
   *  the old key would 409 `idempotency_conflict`) -- buildCommitRequest
   *  mints one per call, so re-building the request handles it. */
  confirm_duplicate?: boolean
  /** Snapshot of FE-displayed totals for the price-drift gate. Strongly
   *  recommended on `pay` and `defer_payment`; ignored on `hold` (no
   *  charge to drift against). */
  expected_totals?: {
    total: string | number
    taxes?: string | number
    subtotal?: string | number
    discount_amount?: string | number
  }
}

/** Convert a Cart to the request payload /cart/preview and /cart/commit
 *  expect. Pure -- can be called from anywhere, including event handlers.
 *
 *  Discount mode is mutually exclusive on the wire: backend silently drops
 *  one side when both are present. We resolve the conflict here so the
 *  caller doesn't have to: cart-level wins (it's the broader scope), and
 *  per-line `discount_amount` is dropped when `cart.discount` is set.
 *
 *  Wire convention for `items[].discount_amount`: PER-UNIT dollar value.
 *  The v2 cart pipeline multiplies it by `qty` server-side. Locally we
 *  store the line-total (per-unit × qty) on each cart line for historical
 *  reasons, so we divide back out by qty here to match the wire contract.
 *  Sending the line-total causes the BE to double-apply the discount
 *  (line-total × qty), driving subtotals negative. */
export function cartToRequest(
  cart: Cart,
  approvals: readonly DiscountApproval[] = discountApprovalSource(),
): CartPreviewRequest {
  const cartLevelActive = !!cart.discount
  const req: CartPreviewRequest = {
    customer_id: cart.customer_id,
    exempt_taxes: cart.exempt_taxes,
    items: cart.lines.map((l) => {
      const item: CartPreviewRequest['items'][number] = {
        product_id: l.product_id,
        qty: l.qty,
        lot_number: l.lot_number,
        price: l.manual_price ?? null,
      }
      if (!cartLevelActive && l.discount_amount != null && l.discount_amount > 0) {
        const absQty = Math.max(1, Math.abs(l.qty || 1))
        item.discount_amount = +(l.discount_amount / absQty).toFixed(4)
        // Reason audit trail rides with the discount it explains.
        if (l.discount_reason_id != null) item.discount_reason_id = l.discount_reason_id
        if (l.discount_reason_other) item.discount_reason_other = l.discount_reason_other
      }
      if (l.gift_card_number != null) {
        item.gift_card_number = l.gift_card_number
      }
      if (l.gift_card_balance != null) {
        item.gift_card_balance = l.gift_card_balance
      }
      // Only scan-gated cannabis lines carry the flag; send an explicit bool
      // so /cart/commit persists the confirmed state. Lines without it (the
      // common case) stay off the wire and the BE column default stands.
      if (l.scan_confirmed !== undefined) {
        item.scan_confirmed = l.scan_confirmed === true
      }
      return item
    }),
    ...(cart.discount ? { discount: cart.discount } : {}),
  }
  // Manager approvals: the PIN rides every request whose discount value it
  // approved (preview, re-preview, commit, payment items), and nothing else.
  // Why not "once": see the header of lib/discountLimit.ts.
  return attachApprovals(req, cart, approvals)
}

/** Where `cartToRequest` reads the live approvals from by default. The cart
 *  store registers itself (stores/cartStore.ts) so every caller -- the preview
 *  hook, the self-heal loop, CheckoutModal's commits and payment items --
 *  carries the PIN without threading it through. Injected rather than
 *  imported to keep this module free of store imports (and import cycles). */
let discountApprovalSource: () => readonly DiscountApproval[] = () => []
export function setDiscountApprovalSource(fn: () => readonly DiscountApproval[]): void {
  discountApprovalSource = fn
}

/** The BE receipt this cart is bound to (posStore.currentReceiptId: an
 *  unheld/recalled receipt, or one already committed and partly paid) -- the
 *  same id /cart/commit sends for truncate-replace. Injected like the
 *  approvals above. */
let previewReceiptIdSource: () => number | null = () => null
export function setPreviewReceiptIdSource(fn: () => number | null): void {
  previewReceiptIdSource = fn
}

/** Receives every committed receipt (the /cart/commit response), so the
 *  cart store can keep its `discount_overrides` for "approved by". Injected
 *  like the sources above. */
export interface CommittedReceiptLike {
  id?: unknown
  discount_overrides?: DiscountOverrideRow[]
}
let committedReceiptSink: (receipt: CommittedReceiptLike | null | undefined) => void = () => {}
export function setCommittedReceiptSink(
  fn: (receipt: CommittedReceiptLike | null | undefined) => void,
): void {
  committedReceiptSink = fn
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/** Default debounce in ms. Long enough to absorb a `+qty +qty +qty` burst,
 *  short enough that the preview shows up before the cashier looks away. */
export const PREVIEW_DEBOUNCE_MS = 250

/** Watchdog: if a preview fetch hasn't resolved (success, error, or
 *  abort) within this window, force-fail it to `error`. Without this
 *  upper bound, a request that genuinely hangs at the transport layer
 *  (BE worker stuck, CORS preflight pathological, proxy not responding,
 *  Tornado IOLoop blocked, etc.) leaves `previewStatus` glued to
 *  `'fetching'` -- the Checkout button shows "Calculating…" indefinitely
 *  and the cashier has no recovery short of a page refresh. 10s is
 *  generous for a healthy /cart/preview (typical p99 well under 1s in
 *  production) but bounded enough that the cashier won't sit on
 *  Calculating long. */
const PREVIEW_WATCHDOG_MS = 10_000

/**
 * Single preview call. Throws V2ApiError on failure.
 *
 * Routes through `v2api.post` so the request inherits the
 * `apiClient.baseURL` (from `sessionStore.backendUrl`) + JWT auth headers
 * + V2 envelope unwrap. We do NOT use raw `fetch()` here -- that would
 * bypass the baseURL and send to the dev-server origin instead of the
 * backend port (1337 locally).
 *
 * `signal` is forwarded to axios so a stale preview is *actually*
 * cancelled at the network layer when the cart shape moves on -- not
 * just dropped client-side. Without this, slow previews would still
 * land on the BE and have it do the work twice for one user action.
 */
export async function fetchCartPreview(
  body: CartPreviewRequest,
  signal?: AbortSignal,
): Promise<PreviewResponse> {
  // Every preview path (debounced hook, self-heal loop, approval check,
  // CheckoutModal's re-prices) funnels through here, so the bound receipt
  // id is attached once, here.
  const receiptId = body.receipt_id ?? previewReceiptIdSource()
  const withReceipt = receiptId != null ? { ...body, receipt_id: receiptId } : body
  return v2api.post<PreviewResponse>(v2('/cart/preview'), withReceipt, { signal })
}

/**
 * Hook: fire /cart/preview on every basket-affecting change, debounced.
 *
 * Contract:
 *  - Watches the supplied `cart`. Fingerprints it (lines + customer + exempt
 *    flag); if the fingerprint hasn't changed since the last fired preview,
 *    no-ops.
 *  - On change, schedules a fetch after PREVIEW_DEBOUNCE_MS. A subsequent
 *    change inside the window cancels the pending fetch.
 *  - In-flight fetches are aborted via AbortController when the cart changes
 *    again, so the cashier's last action always wins.
 *  - Calls `onPreview` with the response and the fingerprint it was for, so
 *    the consumer can ignore stale resolutions if cart has moved on.
 *  - `onError` carries the thrown error; the consumer decides whether to
 *    surface a banner.
 *
 * The store is responsible for setting `previewStatus` on the cart based on
 * the callbacks here -- this hook is pure I/O orchestration.
 */
export function useCartPreview(
  cart: Cart,
  callbacks: {
    onPending?: (fingerprint: string) => void
    onPreview: (preview: PreviewResponse, fingerprint: string) => void
    onError: (err: unknown, fingerprint: string) => void
    /** A response already priced for exactly this fingerprint (a manager
     *  approval's preview, see cartStore.primePreview). When it returns one,
     *  it is landed instead of fetching the same basket again. */
    takePrimed?: (fingerprint: string) => PreviewResponse | null
  },
  enabled = true,
): void {
  const lastFingerprintRef = useRef<string | null>(null)
  const timerRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const watchdogRef = useRef<number | null>(null)
  const cbRef = useRef(callbacks)
  // Latch the callbacks so the effect doesn't re-run on every render the
  // consumer triggers; only cart-fingerprint changes trigger work.
  cbRef.current = callbacks

  const fingerprint = cartFingerprint(cart)
  const isEmpty = cart.lines.length === 0

  useEffect(() => {
    if (!enabled) {
      // Disabled (e.g. user entered refund mode -- the bridge passes
      // `!isRefundMode` as enabled). Any timer/fetch/watchdog from a
      // prior enabled-true run must be cancelled here, otherwise a
      // 250ms-old debounce timer fires AFTER disable, completes a
      // /cart/preview, and lands an applyPreview() that clobbers the
      // refund-mirror's preview write at useV2PreviewBridge.ts:69-94.
      // Matches the cleanup the `isEmpty` branch below does for the
      // same reason.
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (abortRef.current !== null) {
        abortRef.current.abort()
        abortRef.current = null
      }
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      return
    }
    if (isEmpty) {
      // Empty cart: nothing to preview. Cancel any in-flight work.
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (abortRef.current !== null) {
        abortRef.current.abort()
        abortRef.current = null
      }
      lastFingerprintRef.current = null
      return
    }
    if (fingerprint === lastFingerprintRef.current) {
      // Cycled back to the last scheduled shape -- no preview needed.
      // We record the fingerprint at SCHEDULE time below so re-renders
      // inside the debounce window early-return here instead of
      // cancelling and rescheduling on every benign cart-identity flip
      // (e.g. setPreviewPending toggling status without changing math).
      return
    }

    // Schedule. Reset any prior timer; abort any in-flight fetch from the
    // previous fingerprint.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (abortRef.current !== null) {
      abortRef.current.abort()
      abortRef.current = null
    }
    const primed = cbRef.current.takePrimed?.(fingerprint) ?? null
    if (primed) {
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current)
        watchdogRef.current = null
      }
      lastFingerprintRef.current = fingerprint
      cbRef.current.onPreview(primed, fingerprint)
      return
    }
    cbRef.current.onPending?.(fingerprint)

    // Latch the fingerprint immediately so re-renders in the debounce
    // window match it at the early-return above. NB: the catch handler
    // does NOT reset this on abort -- by the time the abort microtask
    // fires, the new effect run has already overwritten lastFingerprintRef
    // with the *new* fingerprint. Resetting here would clobber the new
    // value with null and cause the next benign re-render to schedule a
    // duplicate fetch.
    lastFingerprintRef.current = fingerprint

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      const ctrl = new AbortController()
      abortRef.current = ctrl
      const firedFor = fingerprint
      const body = cartToRequest(cart)

      // Watchdog: force-abort if the fetch hasn't settled by
      // PREVIEW_WATCHDOG_MS. Distinguished from a same-shape supersede
      // (the .catch's `ctrl.signal.aborted` early-return) by the
      // `watchdogFired` flag so this path actually surfaces onError
      // instead of going silent. Without this, a hung transport leaves
      // previewStatus glued to 'fetching' and the Checkout button shows
      // "Calculating…" forever -- the cashier has no recovery short of
      // a page refresh.
      let watchdogFired = false
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current)
      }
      // Capture this fetch's watchdog id locally. The ref is updated to
      // the new id below, but a LATE-arriving .then/.catch from a
      // superseded prior fetch could otherwise call clearWatchdog and
      // see `watchdogRef.current !== null` (now pointing at OUR new
      // watchdog) and clear it -- leaving this fetch unprotected for
      // up to 10s. Guarding on `myWatchdogId` rather than the ref's
      // current value pins each clearWatchdog to the watchdog it was
      // born paired with.
      const myWatchdogId = window.setTimeout(() => {
        watchdogRef.current = null
        watchdogFired = true
        ctrl.abort()
      }, PREVIEW_WATCHDOG_MS)
      watchdogRef.current = myWatchdogId

      const clearWatchdog = () => {
        if (watchdogRef.current === myWatchdogId) {
          window.clearTimeout(myWatchdogId)
          watchdogRef.current = null
        }
      }

      fetchCartPreview(body, ctrl.signal)
        .then((preview) => {
          clearWatchdog()
          if (ctrl.signal.aborted) return
          cbRef.current.onPreview(preview, firedFor)
        })
        .catch((err) => {
          clearWatchdog()
          // Cancellation: silent -- the next preview will fire on its
          // own when the next fingerprint change comes through. Both
          // browser-native AbortError and axios's CanceledError land here
          // when the signal fires, so check the signal directly rather
          // than name-matching either constructor. EXCEPT when the
          // watchdog fired the abort: that's a hung transport that
          // wouldn't otherwise self-recover, so we surface it as an
          // error so the cashier sees something other than "Calculating"
          // and can retry by adding/removing an item (or refreshing).
          if (ctrl.signal.aborted && !watchdogFired) {
            return
          }
          cbRef.current.onError(err, firedFor)
        })
    }, PREVIEW_DEBOUNCE_MS)

    // NB: NO cleanup function here. Earlier this effect cleared the
    // pending timer on every dep-change, but the `cart` dep includes
    // the parent's v2Cart object whose identity can flip without the
    // fingerprint changing (e.g. the bridge re-projects customer on a
    // React Query refetch). When that happens the effect re-runs,
    // returns early at the "fingerprint === lastFingerprintRef.current"
    // check above, and the prior cleanup already nuked the timer -- no
    // new timer is scheduled, the onPreview/onError callbacks never
    // fire, and previewStatus is stuck at 'fetching' forever. The
    // observable symptom was the Checkout button glued to "Calculating"
    // after a CheckoutModal close on a partial-payment flow. Letting
    // the timer ride is safe: if the cart shape genuinely changed, the
    // next effect run aborts the in-flight fetch (the `abortRef.abort()`
    // block above) before scheduling its own; if the shape is the same,
    // the captured `cart` produces an identical request body. Unmount
    // is handled by the dedicated unmount-cleanup effect below.
  }, [enabled, fingerprint, isEmpty, cart])

  // Final cleanup on unmount: cancel everything.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
      }
      if (abortRef.current !== null) {
        abortRef.current.abort()
      }
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current)
      }
      // Clear the latch on unmount so a remount with an unchanged cart
      // reschedules instead of hitting the "fingerprint === last" early
      // return. This only matters under StrictMode's dev double-invoke,
      // which reuses the same refs across its synthetic unmount/remount:
      // mount #1 latches the fingerprint and arms the timer, the synthetic
      // unmount cancels the timer above, then the remount early-returns on
      // the still-latched fingerprint and never fires the preview --
      // leaving previewStatus stuck at 'fetching' until the bridge
      // watchdog flips it to 'error' (tax shows $0 on refresh). A genuine
      // unmount destroys the refs, so this is inert in production.
      lastFingerprintRef.current = null
    }
  }, [])
}

// ---------------------------------------------------------------------------
// Commit
// ---------------------------------------------------------------------------

/**
 * Mutation: POST /cart/commit. Returns the canonical receipt envelope on
 * success; throws V2ApiError on price_drift / sanity_check_failed / etc.
 *
 * Caller is responsible for generating + persisting the idempotency_key
 * across retries. Use `idempotencyKey()` for a fresh key per logical intent;
 * reuse it across automatic retries of the same payload.
 */
export function useCommitCart() {
  return useMutation({
    mutationFn: async (body: CartCommitRequest) => {
      const res = await v2api.post<V2Envelope<{ receipt: unknown }>>(
        v2('/cart/commit'),
        body,
      )
      try {
        committedReceiptSink(
          (res as { data?: { receipt?: CommittedReceiptLike } } | undefined)?.data?.receipt,
        )
      } catch {
        /* display-only bookkeeping must never fail a commit */
      }
      return res
    },
  })
}

/** Build a CartCommitRequest from a Cart + outcome (hold|pay|commit). Caller
 *  mints the idempotency key and supplies expected_totals from the latest
 *  preview.
 *
 *  - `{ hold: true }` — persist as a held receipt, no JT.
 *  - `{ pay: ... }` — single-shot create + first payment.
 *  - `{ commit: true }` — `defer_payment` mode: create the receipt
 *    only; payment lands later (used by the integrated card-terminal
 *    flow, which then POSTs to `/receipts/{id}/terminal-payments`).
 *
 *  When the cart is bound to an existing BE receipt (`opts.receiptId` --
 *  the cashier's `currentReceiptId`, set for BOTH an unholded receipt and
 *  a fresh commit whose payment hasn't completed), the request includes
 *  `receipt_id` so the BE truncate-replaces that receipt's items in place
 *  instead of creating a sibling. A null receiptId (fresh sale) omits it,
 *  so the BE creates a new receipt. */
export function buildCommitRequest(
  cart: Cart,
  outcome:
    | { hold: true }
    | { pay: CartCommitRequest['pay'] }
    | { commit: true },
  opts: {
    idempotencyKey?: string
    receiptId?: number | null
    /** Cashier confirmed the `possible_duplicate` dialog -- override the
     *  BE duplicate-order guard on this retry. See CartCommitRequest. */
    confirmDuplicate?: boolean
  } = {},
): CartCommitRequest {
  const base = cartToRequest(cart)
  const req: CartCommitRequest = {
    ...base,
    idempotency_key: opts.idempotencyKey ?? idempotencyKey(),
  }
  if (opts.receiptId != null) {
    req.receipt_id = opts.receiptId
  }
  if (opts.confirmDuplicate) {
    req.confirm_duplicate = true
  }
  if ('hold' in outcome) {
    req.hold = true
    return req
  }
  // Pay or commit-only: both want the price-drift gate. The BE compares
  // expected_totals against its own pricing pass and rejects with
  // `price_drift` if they disagree. Without it the gate is skipped —
  // dangerous on pay, and still useful on commit-only since the cashier
  // is about to charge a card against this receipt.
  const pt = cart.preview?.totals
  if (pt) {
    req.expected_totals = {
      total: pt.total,
      taxes: pt.taxes,
      subtotal: pt.subtotal,
      discount_amount: pt.discount_amount,
    }
  }
  if ('pay' in outcome) {
    req.pay = outcome.pay
  } else {
    // `commit` outcome → tell the BE to leave the receipt unpaid so
    // the FE can attach a terminal-payments session next. Mutually
    // exclusive with `pay`; sending both is a 400.
    req.defer_payment = true
  }
  return req
}
